import { useSalesSummaryPageLazyQuery } from '../../../../services';
import {
    CTableWrapper,
    DBlock,
    DBlockHeader,
    DCheckbox,
    DPage,
    DPageBody,
    DYear,
    useDYear,
} from '../../../dum';
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    ButtonBase,
    Chip,
    Button,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DownloadIcon from '@mui/icons-material/Download';
import {
    BUSINESS_TZ,
    formatFloat,
    getDateRange,
    useTouchDoubleActivation,
} from '../../../../helpers';
import dayjs from 'dayjs';
import { downloadSalesSummaryReport } from './sales-summary-excel-export-button';
import { downloadOrderRequestSummaryReport } from './order-request-summary-excel-export-button';
import {
    ChangeDisplayMode,
    ValueWithChange,
} from './sales-summary-change';
import { SalesSummaryAuditTable } from './sales-summary-audit-table';

// A month row carries this year's figure and the same month one year earlier
// (prev_*), so every cell can show its year-over-year change inline.
type TotalRange = { year: number; month: number } & {
    total: number;
    total_with_tax: number;
    tax: number;
    kilos_sold: number;
    prev_total: number;
    prev_total_with_tax: number;
    prev_tax: number;
    prev_kilos_sold: number;
    cumulative_total: number;
    prev_cumulative_total: number;
};

// A dimension breakdown row (Categoría or Material) keyed dynamically by the
// dimension id (kilos), with a parallel `prev_<id>` holding last year's kilos.
// '0' / 'prev_0' are the "Otros" bucket (sales whose product has no value for
// that dimension).
type CategoryRange = Record<string, number> & { year: number; month: number };

const kilosFormat = (value: number) => formatFloat(value) + ' kg';

// Sum a row's kilos across the visible dimension columns plus the Otros bucket.
// Dimension-generic (works for categories and materials alike) — it only reads
// `<id>` / `prev_<id>` keys and the '0' bucket.
function sumVisibleDimensionKilos(
    item: CategoryRange,
    dimensions: { id: number }[],
    previous = false,
): number {
    return (
        dimensions.reduce(
            (sum, dimension) =>
                sum +
                (item[
                    previous ? `prev_${dimension.id}` : String(dimension.id)
                ] || 0),
            0,
        ) + (item[previous ? 'prev_0' : '0'] || 0)
    );
}

function SalesSummaryMonthButton({
    item,
    isCurrent,
    onOpen,
}: {
    item: TotalRange;
    isCurrent: boolean;
    onOpen: () => void;
}) {
    const handleTouchEnd = useTouchDoubleActivation(onOpen);
    return (
        <ButtonBase
            onDoubleClick={onOpen}
            onTouchEnd={handleTouchEnd}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderRadius: 1,
                px: 0.5,
                touchAction: 'manipulation',
            }}
        >
            {dayjs().set('month', item.month - 1).format('MMMM')}
            {isCurrent ? (
                <Chip
                    label={'Actual'}
                    size={'small'}
                    color={'primary'}
                    sx={{
                        height: 16,
                        fontSize: '0.625rem',
                        pointerEvents: 'none',
                        '& .MuiChip-label': { px: 0.6 },
                    }}
                />
            ) : null}
        </ButtonBase>
    );
}

function SalesSummaryActionsMenu({
    sales,
    orderRequestRecords,
    disabled,
    changeDisplay,
    setChangeDisplay,
    includeReconciliationOnly,
    setIncludeReconciliationOnly,
}: {
    sales?: Parameters<typeof downloadSalesSummaryReport>[0];
    orderRequestRecords?: Parameters<
        typeof downloadOrderRequestSummaryReport
    >[0];
    disabled: boolean;
    changeDisplay: ChangeDisplayMode;
    setChangeDisplay: (mode: ChangeDisplayMode) => void;
    includeReconciliationOnly: boolean;
    setIncludeReconciliationOnly: (value: boolean) => void;
}) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const close = () => setAnchorEl(null);

    return (
        <>
            <Button
                variant={'outlined'}
                size={'small'}
                endIcon={<ArrowDropDownIcon />}
                disabled={disabled}
                onClick={(event) => setAnchorEl(event.currentTarget)}
            >
                Acciones
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem
                    disabled={disabled}
                    onClick={() => {
                        close();
                        void downloadSalesSummaryReport(sales);
                    }}
                >
                    <ListItemIcon>
                        <DownloadIcon fontSize={'small'} />
                    </ListItemIcon>
                    <ListItemText>Descargar resumen de ventas</ListItemText>
                </MenuItem>
                <MenuItem
                    disabled={disabled}
                    onClick={() => {
                        close();
                        void downloadOrderRequestSummaryReport(
                            orderRequestRecords,
                        );
                    }}
                >
                    <ListItemIcon>
                        <DownloadIcon fontSize={'small'} />
                    </ListItemIcon>
                    <ListItemText>
                        Descargar resumen de pedidos
                    </ListItemText>
                </MenuItem>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        px: 2,
                        py: 1,
                    }}
                    onClick={(event) => event.stopPropagation()}
                >
                    <Typography variant={'body2'}>
                        Mostrar valores como
                    </Typography>
                    <ToggleButtonGroup
                        size={'small'}
                        exclusive
                        value={changeDisplay}
                        onChange={(_, value: ChangeDisplayMode | null) => {
                            if (value) setChangeDisplay(value);
                        }}
                        aria-label={'Modo de comparación del resumen'}
                    >
                        <ToggleButton value={'difference'}>
                            Diferencia
                        </ToggleButton>
                        <ToggleButton value={'percentage'}>% cambio</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <Box
                    sx={{ px: 2, pb: 1 }}
                    onClick={(event) => event.stopPropagation()}
                >
                    <DCheckbox
                        value={includeReconciliationOnly}
                        label={'Incluir solo conciliación'}
                        onChange={setIncludeReconciliationOnly}
                        disableGutters
                    />
                </Box>
            </Menu>
        </>
    );
}

export function SalesSummary() {
    const businessYear = dayjs().tz(BUSINESS_TZ).year();
    const [totalRange, setTotalRange] = useState<TotalRange[]>([]);
    const [productCategoryRange, setProductCategoryRange] = useState<
        CategoryRange[]
    >([]);
    // The filtered categories that become the Categorías columns; kept in state
    // so the header columns and the totals row are built from one source.
    const [categories, setCategories] = useState<
        { id: number; name: string }[]
    >([]);
    // Material mirrors Categoría exactly: one kilos column per material plus an
    // "Otros" bucket for sales whose product has no material. Same DimensionRange
    // shape (id / prev_id keys) so it reuses sumVisibleDimensionKilos.
    const [productMaterialRange, setProductMaterialRange] = useState<
        CategoryRange[]
    >([]);
    const [materials, setMaterials] = useState<
        { id: number; name: string }[]
    >([]);
    const [includeReconciliationOnly, setIncludeReconciliationOnly] =
        useState(false);
    const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
    const [changeDisplay, setChangeDisplay] =
        useState<ChangeDisplayMode>('difference');

    const { year, setYear } = useDYear({
        COMPONENT_KEY: SalesSummary.name + '_Year',
        defaultValue: businessYear,
    });

    const [query, { data, loading }] = useSalesSummaryPageLazyQuery({
        onCompleted: (data) => {
            if (!year) {
                setTotalRange([]);
                setProductCategoryRange([]);
                setCategories([]);
                setProductMaterialRange([]);
                setMaterials([]);
                return;
            }

            // ----- Totales table -----
            const totalMonthRange = getDateRange({
                month: null,
                year: year,
                content: {
                    total: 0,
                    total_with_tax: 0,
                    tax: 0,
                    kilos_sold: 0,
                    prev_total: 0,
                    prev_total_with_tax: 0,
                    prev_tax: 0,
                    prev_kilos_sold: 0,
                    cumulative_total: 0,
                    prev_cumulative_total: 0,
                },
            });

            data.getTotalSalesSummary.sales.forEach((sale) => {
                const found = totalMonthRange.find(
                    (mr) => mr.year === sale.year && mr.month === sale.month,
                );
                if (found) {
                    found.total = sale.total;
                    found.tax = sale.tax;
                    found.total_with_tax = sale.total_with_tax;
                    found.kilos_sold = sale.kilos_sold;
                }
            });

            // Previous-year rows carry the prior year, so match on month alone.
            data.getPreviousTotalSalesSummary.sales.forEach((sale) => {
                const found = totalMonthRange.find(
                    (mr) => mr.month === sale.month,
                );
                if (found) {
                    found.prev_total = sale.total;
                    found.prev_tax = sale.tax;
                    found.prev_total_with_tax = sale.total_with_tax;
                    found.prev_kilos_sold = sale.kilos_sold;
                }
            });

            let cumulativeTotal = 0;
            let previousCumulativeTotal = 0;
            totalMonthRange.forEach((monthRow) => {
                cumulativeTotal += monthRow.total;
                previousCumulativeTotal += monthRow.prev_total;
                monthRow.cumulative_total = cumulativeTotal;
                monthRow.prev_cumulative_total = previousCumulativeTotal;
            });

            // ----- Categorías table -----
            const filteredCategories = data.getProductCategories.filter(
                (pc) =>
                    pc.order_production_type_id === 1 ||
                    !pc.order_production_type_id,
            );

            const categoriesDictionary = filteredCategories.reduce(
                (acc, curr) => {
                    acc[String(curr.id)] = 0;
                    acc[`prev_${curr.id}`] = 0;
                    return acc;
                },
                {} as Record<string, number>,
            );
            categoriesDictionary['0'] = 0;
            categoriesDictionary['prev_0'] = 0;

            const categoryMonthRange = getDateRange({
                month: null,
                year: year,
                content: categoriesDictionary,
            });

            data.getProductCategorySalesSummary.sales.forEach((sale) => {
                const found = categoryMonthRange.find(
                    (dr) => dr.year === sale.year && dr.month === sale.month,
                );
                if (found) {
                    const key = sale.product_category_id
                        ? String(sale.product_category_id)
                        : '0';
                    found[key] += sale.kilos_sold;
                }
            });

            data.getPreviousProductCategorySalesSummary.sales.forEach(
                (sale) => {
                    const found = categoryMonthRange.find(
                        (dr) => dr.month === sale.month,
                    );
                    if (found) {
                        const key = sale.product_category_id
                            ? `prev_${sale.product_category_id}`
                            : 'prev_0';
                        found[key] += sale.kilos_sold;
                    }
                },
            );

            setCategories(
                filteredCategories.map((pc) => ({ id: pc.id, name: pc.name })),
            );
            setProductCategoryRange(categoryMonthRange as CategoryRange[]);

            // ----- Material table (mirrors Categorías) -----
            // No order-production-type filter here: material applies across all
            // product types, unlike categories which the block scopes to type 1.
            const allMaterials = data.getProductMaterials;

            const materialsDictionary = allMaterials.reduce(
                (acc, curr) => {
                    acc[String(curr.id)] = 0;
                    acc[`prev_${curr.id}`] = 0;
                    return acc;
                },
                {} as Record<string, number>,
            );
            materialsDictionary['0'] = 0;
            materialsDictionary['prev_0'] = 0;

            const materialMonthRange = getDateRange({
                month: null,
                year: year,
                content: materialsDictionary,
            });

            data.getProductMaterialSalesSummary.sales.forEach((sale) => {
                const found = materialMonthRange.find(
                    (dr) => dr.year === sale.year && dr.month === sale.month,
                );
                if (found) {
                    const key = sale.product_material_id
                        ? String(sale.product_material_id)
                        : '0';
                    found[key] += sale.kilos_sold;
                }
            });

            data.getPreviousProductMaterialSalesSummary.sales.forEach(
                (sale) => {
                    const found = materialMonthRange.find(
                        (dr) => dr.month === sale.month,
                    );
                    if (found) {
                        const key = sale.product_material_id
                            ? `prev_${sale.product_material_id}`
                            : 'prev_0';
                        found[key] += sale.kilos_sold;
                    }
                },
            );

            setMaterials(
                allMaterials.map((pm) => ({ id: pm.id, name: pm.name })),
            );
            setProductMaterialRange(materialMonthRange as CategoryRange[]);

            setTotalRange(totalMonthRange as TotalRange[]);
        },
    });

    useEffect(() => {
        void query({
            variables: {
                year: year,
                previousYear: year ? year - 1 : null,
                includeReconciliationOnly,
            },
        });
    }, [query, year, includeReconciliationOnly]);

    const businessToday = dayjs().tz(BUSINESS_TZ);
    const currentYear = businessToday.year();
    const currentMonth = businessToday.month() + 1;
    const isCurrentMonth = (item: { year: number; month: number }) =>
        item.year === currentYear && item.month === currentMonth;
    const isFutureMonthWithoutSales = (month: number) =>
        year === currentYear &&
        month > currentMonth &&
        (totalRange.find((item) => item.month === month)?.total ?? 0) === 0;

    const auditSales = useMemo(
        () =>
            selectedMonth === null
                ? []
                : (data?.getProductSalesSummary.sales ?? []).filter(
                      (sale) => sale.month === selectedMonth,
                  ),
        [data, selectedMonth],
    );

    const annualTotals = useMemo(
        () =>
            totalRange.reduce(
            (acc, r) => {
                acc.kilos_sold += r.kilos_sold;
                acc.total += r.total;
                acc.tax += r.tax;
                acc.total_with_tax += r.total_with_tax;
                acc.prev_kilos_sold += r.prev_kilos_sold;
                acc.prev_total += r.prev_total;
                acc.prev_tax += r.prev_tax;
                acc.prev_total_with_tax += r.prev_total_with_tax;
                return acc;
            },
            {
                kilos_sold: 0,
                total: 0,
                tax: 0,
                total_with_tax: 0,
                prev_kilos_sold: 0,
                prev_total: 0,
                prev_tax: 0,
                prev_total_with_tax: 0,
            },
            ),
        [totalRange],
    );

    // Column totals for the Totales table: sum each metric (and its previous-year
    // counterpart) across the twelve months so the totals row can show its own
    // year-over-year change.
    const totalsRowTotales = useMemo(() => {
        const sum = annualTotals;
        return [
            'Total',
            <ValueWithChange
                current={sum.kilos_sold}
                previous={sum.prev_kilos_sold}
                format={kilosFormat}
                changeDisplay={changeDisplay}
            />,
            <ValueWithChange
                current={sum.total}
                previous={sum.prev_total}
                format={formatFloat}
                changeDisplay={changeDisplay}
            />,
            <ValueWithChange
                current={sum.tax}
                previous={sum.prev_tax}
                format={formatFloat}
                changeDisplay={changeDisplay}
            />,
            <ValueWithChange
                current={sum.total_with_tax}
                previous={sum.prev_total_with_tax}
                format={formatFloat}
                changeDisplay={changeDisplay}
            />,
            <ValueWithChange
                current={sum.total}
                previous={sum.prev_total}
                format={formatFloat}
                changeDisplay={changeDisplay}
            />,
        ];
    }, [annualTotals, changeDisplay]);

    // Column totals for the Categorías table: one summed cell per category (plus
    // Otros), each comparing this year's kilos against last year's.
    const totalsRowCategories = useMemo(() => {
        const sumKey = (key: string) =>
            productCategoryRange.reduce((acc, r) => acc + (r[key] || 0), 0);
        return [
            'Total',
            ...categories.map((c) => (
                <ValueWithChange
                    key={`category-total-${c.id}`}
                    current={sumKey(String(c.id))}
                    previous={sumKey(`prev_${c.id}`)}
                    format={kilosFormat}
                    changeDisplay={changeDisplay}
                />
            )),
            <ValueWithChange
                key={'category-total-otros'}
                current={sumKey('0')}
                previous={sumKey('prev_0')}
                format={kilosFormat}
                changeDisplay={changeDisplay}
            />,
            <ValueWithChange
                key={'category-total-all'}
                current={productCategoryRange.reduce(
                    (sum, item) =>
                        sum + sumVisibleDimensionKilos(item, categories),
                    0,
                )}
                previous={productCategoryRange.reduce(
                    (sum, item) =>
                        sum +
                        sumVisibleDimensionKilos(item, categories, true),
                    0,
                )}
                format={kilosFormat}
                changeDisplay={changeDisplay}
            />,
        ];
    }, [productCategoryRange, categories, changeDisplay]);

    // Column totals for the Material table (mirrors totalsRowCategories).
    const totalsRowMaterials = useMemo(() => {
        const sumKey = (key: string) =>
            productMaterialRange.reduce((acc, r) => acc + (r[key] || 0), 0);
        return [
            'Total',
            ...materials.map((m) => (
                <ValueWithChange
                    key={`material-total-${m.id}`}
                    current={sumKey(String(m.id))}
                    previous={sumKey(`prev_${m.id}`)}
                    format={kilosFormat}
                    changeDisplay={changeDisplay}
                />
            )),
            <ValueWithChange
                key={'material-total-otros'}
                current={sumKey('0')}
                previous={sumKey('prev_0')}
                format={kilosFormat}
                changeDisplay={changeDisplay}
            />,
            <ValueWithChange
                key={'material-total-all'}
                current={productMaterialRange.reduce(
                    (sum, item) =>
                        sum + sumVisibleDimensionKilos(item, materials),
                    0,
                )}
                previous={productMaterialRange.reduce(
                    (sum, item) =>
                        sum +
                        sumVisibleDimensionKilos(item, materials, true),
                    0,
                )}
                format={kilosFormat}
                changeDisplay={changeDisplay}
            />,
        ];
    }, [productMaterialRange, materials, changeDisplay]);

    return (
        <DPage>
            <DPageBody disableGutters>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        // Single scroll container anchored to the page-body flex
                        // height so the stacked blocks scroll inside the page
                        // instead of overflowing past the background. DBlock has
                        // flexGrow:1 + minHeight:0, so left unchecked the blocks
                        // distribute/shrink to fit and the last table collapses to
                        // one row — pin each child to its natural height instead.
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        '& > *': { flexShrink: 0, flexGrow: 0 },
                    }}
                >
                    <DBlock>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2,
                            }}
                        >
                            <Box sx={{ mr: 2 }}>
                                <DYear setYear={setYear} year={year} />
                            </Box>
                            <Box sx={{ ml: 'auto' }}>
                                <SalesSummaryActionsMenu
                                    sales={data?.getProductSalesSummary.sales}
                                    orderRequestRecords={
                                        data?.getOrderRequestSummary.records
                                    }
                                    disabled={loading}
                                    changeDisplay={changeDisplay}
                                    setChangeDisplay={setChangeDisplay}
                                    includeReconciliationOnly={
                                        includeReconciliationOnly
                                    }
                                    setIncludeReconciliationOnly={
                                        setIncludeReconciliationOnly
                                    }
                                />
                            </Box>
                        </Box>
                    </DBlock>
                    <DBlock>
                        <DBlockHeader subtitle={'Totales'} />
                        <CTableWrapper
                            items={[
                                {
                                    title: 'Mes',
                                    content: (item) => (
                                        <SalesSummaryMonthButton
                                            item={item}
                                            isCurrent={isCurrentMonth(item)}
                                            onOpen={() =>
                                                setSelectedMonth(item.month)
                                            }
                                        />
                                    ),
                                },
                                {
                                    title: 'Kilos vendidos',
                                    textAlign: 'right',
                                    content: (item) => (
                                        <ValueWithChange
                                            current={item.kilos_sold}
                                            previous={item.prev_kilos_sold}
                                            format={kilosFormat}
                                            changeDisplay={changeDisplay}
                                            showChange={
                                                !isFutureMonthWithoutSales(
                                                    item.month,
                                                )
                                            }
                                        />
                                    ),
                                },
                                {
                                    title: 'Total',
                                    textAlign: 'right',
                                    content: (item) => (
                                        <ValueWithChange
                                            current={item.total}
                                            previous={item.prev_total}
                                            format={formatFloat}
                                            changeDisplay={changeDisplay}
                                            showChange={
                                                !isFutureMonthWithoutSales(
                                                    item.month,
                                                )
                                            }
                                        />
                                    ),
                                },
                                {
                                    title: 'IVA',
                                    textAlign: 'right',
                                    content: (item) => (
                                        <ValueWithChange
                                            current={item.tax}
                                            previous={item.prev_tax}
                                            format={formatFloat}
                                            changeDisplay={changeDisplay}
                                            showChange={
                                                !isFutureMonthWithoutSales(
                                                    item.month,
                                                )
                                            }
                                        />
                                    ),
                                },
                                {
                                    title: 'Total (IVA incluido)',
                                    textAlign: 'right',
                                    content: (item) => (
                                        <ValueWithChange
                                            current={item.total_with_tax}
                                            previous={item.prev_total_with_tax}
                                            format={formatFloat}
                                            changeDisplay={changeDisplay}
                                            showChange={
                                                !isFutureMonthWithoutSales(
                                                    item.month,
                                                )
                                            }
                                        />
                                    ),
                                },
                                {
                                    title: 'Total acumulado',
                                    textAlign: 'right',
                                    content: (item) => (
                                        <ValueWithChange
                                            current={item.cumulative_total}
                                            previous={
                                                item.prev_cumulative_total
                                            }
                                            format={formatFloat}
                                            changeDisplay={changeDisplay}
                                            showChange={
                                                !isFutureMonthWithoutSales(
                                                    item.month,
                                                )
                                            }
                                        />
                                    ),
                                },
                            ]}
                            data={totalRange}
                            totalsRow={totalsRowTotales}
                            loading={loading}
                            rowSx={(item) => ({
                                ...(selectedMonth === item.month
                                    ? { bgcolor: 'action.selected' }
                                    : {}),
                                ...(isCurrentMonth(item)
                                    ? {
                                          borderLeft: '4px solid',
                                          borderLeftColor: 'primary.main',
                                      }
                                    : {}),
                            })}
                        />
                        <Typography
                            variant={'caption'}
                            sx={{
                                color: 'text.secondary',
                                display: 'block',
                                mt: 1,
                            }}
                        >
                            Haz doble clic o doble toque en un mes para auditar
                            las ventas que forman su total.
                        </Typography>
                    </DBlock>
                    {selectedMonth !== null ? (
                        <DBlock>
                            <DBlockHeader
                                subtitle={`Auditoría de ${dayjs()
                                    .set('month', selectedMonth - 1)
                                    .format('MMMM')} ${year ?? ''}`}
                            >
                                <ButtonBase
                                    onClick={() => setSelectedMonth(null)}
                                    sx={{
                                        color: 'text.secondary',
                                        fontSize: '0.75rem',
                                        px: 1,
                                        py: 0.5,
                                    }}
                                >
                                    Cerrar
                                </ButtonBase>
                            </DBlockHeader>
                            <SalesSummaryAuditTable sales={auditSales} />
                        </DBlock>
                    ) : null}
                    <DBlock>
                        <DBlockHeader subtitle={'Categorias'} />
                        <CTableWrapper
                            items={[
                                {
                                    title: 'Mes',
                                    content: (item) => {
                                        return dayjs()
                                            .set('month', item.month - 1)
                                            .format('MMMM');
                                    },
                                },
                                ...categories.map((c) => ({
                                    title: c.name,
                                    textAlign: 'right' as const,
                                    content: (item: CategoryRange) => (
                                        <ValueWithChange
                                            current={item[String(c.id)]}
                                            previous={item[`prev_${c.id}`]}
                                            format={kilosFormat}
                                            changeDisplay={changeDisplay}
                                            showChange={
                                                !isFutureMonthWithoutSales(
                                                    item.month,
                                                )
                                            }
                                        />
                                    ),
                                })),
                                {
                                    title: 'Otros',
                                    textAlign: 'right' as const,
                                    content: (item: CategoryRange) => (
                                        <ValueWithChange
                                            current={item['0']}
                                            previous={item['prev_0']}
                                            format={kilosFormat}
                                            changeDisplay={changeDisplay}
                                            showChange={
                                                !isFutureMonthWithoutSales(
                                                    item.month,
                                                )
                                            }
                                        />
                                    ),
                                },
                                {
                                    title: 'Total',
                                    textAlign: 'right' as const,
                                    content: (item: CategoryRange) => (
                                        <ValueWithChange
                                            current={sumVisibleDimensionKilos(
                                                item,
                                                categories,
                                            )}
                                            previous={sumVisibleDimensionKilos(
                                                item,
                                                categories,
                                                true,
                                            )}
                                            format={kilosFormat}
                                            changeDisplay={changeDisplay}
                                            showChange={
                                                !isFutureMonthWithoutSales(
                                                    item.month,
                                                )
                                            }
                                        />
                                    ),
                                },
                            ]}
                            data={productCategoryRange}
                            totalsRow={totalsRowCategories}
                            loading={loading}
                        />
                    </DBlock>
                    <DBlock>
                        <DBlockHeader subtitle={'Material'} />
                        <CTableWrapper
                            items={[
                                {
                                    title: 'Mes',
                                    content: (item) => {
                                        return dayjs()
                                            .set('month', item.month - 1)
                                            .format('MMMM');
                                    },
                                },
                                ...materials.map((m) => ({
                                    title: m.name,
                                    textAlign: 'right' as const,
                                    content: (item: CategoryRange) => (
                                        <ValueWithChange
                                            current={item[String(m.id)]}
                                            previous={item[`prev_${m.id}`]}
                                            format={kilosFormat}
                                            changeDisplay={changeDisplay}
                                            showChange={
                                                !isFutureMonthWithoutSales(
                                                    item.month,
                                                )
                                            }
                                        />
                                    ),
                                })),
                                {
                                    title: 'Otros',
                                    textAlign: 'right' as const,
                                    content: (item: CategoryRange) => (
                                        <ValueWithChange
                                            current={item['0']}
                                            previous={item['prev_0']}
                                            format={kilosFormat}
                                            changeDisplay={changeDisplay}
                                            showChange={
                                                !isFutureMonthWithoutSales(
                                                    item.month,
                                                )
                                            }
                                        />
                                    ),
                                },
                                {
                                    title: 'Total',
                                    textAlign: 'right' as const,
                                    content: (item: CategoryRange) => (
                                        <ValueWithChange
                                            current={sumVisibleDimensionKilos(
                                                item,
                                                materials,
                                            )}
                                            previous={sumVisibleDimensionKilos(
                                                item,
                                                materials,
                                                true,
                                            )}
                                            format={kilosFormat}
                                            changeDisplay={changeDisplay}
                                            showChange={
                                                !isFutureMonthWithoutSales(
                                                    item.month,
                                                )
                                            }
                                        />
                                    ),
                                },
                            ]}
                            data={productMaterialRange}
                            totalsRow={totalsRowMaterials}
                            loading={loading}
                        />
                    </DBlock>
                </Box>
            </DPageBody>
        </DPage>
    );
}
